import type { ShareResourceConfig } from '../types';
import type { PlanScheduleMap } from '../types/domain/plan.types';

/**
 * Share Model
 *
 * Sharing a workout or a plan with friends — the `/share` request and the
 * words around it — shared by web's ShareModal and plan share page and mobile's
 * share screens, so both platforms send the same body and say the same things.
 *
 * The wording helpers take NAMES rather than recipients: web names a friend by
 * their display name, mobile by their @handle (its friend search returns no
 * picture, so the handle is what identifies them for sure). Each platform
 * passes the names it shows.
 */

/** What can be shared. */
export type ShareResourceType = 'plan' | 'workout';

/** The note's ceiling — the API's own. */
export const SHARE_NOTE_MAX_LENGTH = 500;

/** "@mayao" — a friend named by their handle. */
export function formatHandle(username: string): string {
  return `@${username}`;
}

/**
 * "Maya", "Maya and Abe", "Maya and 3 others" — or null when nobody is picked.
 * Null rather than a stand-in, so callers say the empty case in their own
 * words: an earlier "them" rendered "Plan them's weeks".
 */
export function formatRecipientList(names: string[]): string | null {
  if (names.length === 0) return null;
  if (names.length === 1) return names[0] ?? null;
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names[0]} and ${names.length - 1} others`;
}

/** "Maya gets this plan in their inbox." — null when nobody is picked. */
export function getShareConfirmation(
  names: string[],
  type: ShareResourceType
): { who: string; rest: string } | null {
  const who = formatRecipientList(names);
  if (!who) return null;
  return {
    who,
    rest: `${names.length === 1 ? 'gets' : 'get'} this ${type} in their inbox.`,
  };
}

/** The toast once a share has gone. */
export function getSharedMessage(names: string[]): string {
  return `Shared with ${formatRecipientList(names) ?? 'your friends'}`;
}

/** The toast once a plan has been sent with its schedule. */
export function getPlanSentMessage(names: string[]): string {
  return `Plan sent to ${formatRecipientList(names) ?? 'your friends'}.`;
}

/**
 * The sentence under "Plan their weeks": what is being asked, and of whom.
 * With nothing scheduled it says a schedule is optional; with nobody picked
 * the subject is "They".
 */
export function getShareLede(names: string[], sessionCount: number): string {
  if (sessionCount === 0) {
    return 'Give each workout a day, or send it without a schedule and let them pick their own.';
  }
  return `${formatRecipientList(names) ?? 'They'} will be asked to train ${sessionCount} time${
    sessionCount === 1 ? '' : 's'
  }, on the days you set here.`;
}

/**
 * The `/share` request body. A schedule is left out rather than sent empty: a
 * share with no schedule is a plain share, and `{}` would read as "an empty
 * schedule was intended".
 */
export function buildShareConfig(
  target: { type: ShareResourceType; id: string },
  recipients: { value: string }[],
  note: string,
  schedule?: PlanScheduleMap
): ShareResourceConfig {
  return {
    share_with: recipients.map((recipient) => recipient.value),
    share_id: target.id,
    share_type: target.type,
    note: note.trim(),
    include_notes: false,
    ...(schedule && Object.keys(schedule).length > 0 ? { schedule } : {}),
  };
}

/** A plan's line on the share card: "Easy · 24 workouts · 8 weeks". */
export function getPlanShareMeta(plan: {
  difficulty: string;
  workouts: unknown[];
  total_weeks: number | null;
}): string {
  const count = plan.workouts.length;
  const parts = [
    plan.difficulty,
    `${count} ${count === 1 ? 'workout' : 'workouts'}`,
  ];
  // `total_weeks` can be null — only said when set.
  if (plan.total_weeks) {
    parts.push(
      `${plan.total_weeks} ${plan.total_weeks === 1 ? 'week' : 'weeks'}`
    );
  }
  return parts.join(' · ');
}
